import React, {
    useMemo,
    useContext,
    createContext,
    useState,
    useCallback,
    useEffect
} from "react";
import { SiteContext as SiteContextProps } from "./types";
import { axiosclient } from "../data";
import EmailModal from "./components/EmailModal";
import { useLocation } from "@reach/router";
import Cookies from "js-cookie";
import { useQuery } from "@apollo/client";
import { generateQuery, LocationClass } from "gatsby-source-patstore";
import { ColoproktologenUser } from "@types";

const SiteContext = createContext({
    siteTitle: "",
    siteDescription: "",
    contactOpen: false,
    applicationOpen: false,
    setApplicationOpen: () => null,
    loading: false,
    user: undefined,
    getUser: async () => {
        await axiosclient().get("/users/me");
    },
    locations: [],
    users: []
} as SiteContextProps);

export const SiteContextProvider = ({
    children
}: {
  children: React.ReactNode;
}) => {
    const [loading, setLoading] = useState(true);
    const [locations, setLocations] = useState<LocationClass[]>([]);
    const [users, setUsers] = useState<ColoproktologenUser[]>([]);

    const { data } = useQuery(generateQuery({
        type: "find",
        objectName: "Location",
        queryName: "locations",
        fields: ["objectId", "title", "description", "coordinates { latitude, longitude }", "data"]
    }), {
        variables: {
            first: 1000,
            project: {
                have: {
                    id: { equalTo: process.env.GATSBY_PATSTORE_PROJECT_ID }
                }
            }
        },
    
    });

    const findUsers = useCallback(() => {
        axiosclient().post("/functions/find_bcd_specialists")
            .then((response) => {
                setUsers(response.data.result);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    useEffect(() => {
        if (users.length === 0) {
            findUsers();
        }
        if (data) {
            setLocations(data.locations.edges.map((edge: any) => edge.node));
        }
    }, [findUsers, data, users]);

    const [user, setUser] = useState<ColoproktologenUser>();
    const { pathname } = useLocation();

    const [applicationOpen, setApplicationOpen] = useState(false);

    const getUser = useCallback(async () => {
        await axiosclient()
            .get("/users/me")
            .then((response) => {
                setUser(response.data as ColoproktologenUser);
                setLoading(false);
            })
            .catch((error) => {
                if (process.env.NODE_ENV === "development") {
                    console.error(error);
                }
                setLoading(false);
            });
    }, []);

    const localToken = Cookies.get(process.env.GATSBY_SESSION_TOKEN as string);
    useEffect(() => {
        if (!user && localToken ) {
            getUser();
        }
    }, [pathname, localToken]);

    const siteContext = useMemo(() => {
        return {
            siteTitle: "My Site",
            siteDescription: "My site description",
            applicationOpen,
            setApplicationOpen,
            loading,
            user,
            getUser,
            locations,
            users
        };
    }, [applicationOpen, user, loading, getUser, locations, users]);

    const [emailPopup, setEmailPopup] = useState(false);

    useEffect(() => {
        if (pathname.includes("/app") && user && !user.email) {
            setEmailPopup(true);
        }
    }, [user, pathname]);

    return (
        <SiteContext.Provider value={siteContext}>
            {children}
            {user && (
                <EmailModal
                    emailPopup={emailPopup}
                    setEmailPopup={setEmailPopup}
                    getUser={getUser}
                    userId={user?.objectId}
                />
            )}
        </SiteContext.Provider>
    );
};

export const useSiteContext = () => {
    return useContext(SiteContext);
};
